// src/sectors/pilates/attendance/ui/AttendanceMarkModal/useAttendanceMarkFlow.js
import { useEffect, useMemo, useState, useCallback } from 'react';
import { attendanceApi } from '../../../api/attendanceApi'; // path'ini projene göre düzelt
import { S } from './attendanceConstants';
import {
  backendToUiStatus,
  requiresNote,
  uiStatusToBackend,
  hasWarningsOrActions,
} from './attendanceUtils';

export function useAttendanceMarkFlow({
  open,
  sessionId,
  fetchParticipants,
  peopleFetchLimit = 5,
}) {
  const [loading, setLoading] = useState(false);
  const [people, setPeople] = useState([]);
  const [byPerson, setByPerson] = useState({});

  const [saveLoading, setSaveLoading] = useState(false);
  const [decision, setDecision] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const [decisionModalOpen, setDecisionModalOpen] = useState(false);
  const [pendingCommit, setPendingCommit] = useState(false);

  const resetUi = useCallback(() => {
    setDecision(null);
    setDecisionModalOpen(false);
    setPendingCommit(false);
    setSubmitError(null);
  }, []);

  // ===== fetch participants =====
  useEffect(() => {
    let alive = true;

    // Modal kapalıysa state temizle
    if (!open) {
      setLoading(false);
      setSaveLoading(false);
      setPeople([]);
      setByPerson({});
      resetUi();
      return () => {
        alive = false;
      };
    }

    resetUi();

    if (!sessionId) {
      setPeople([]);
      setByPerson({});
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    const initFromList = (list) => {
      const safe = Array.isArray(list) ? list : [];
      setPeople(safe);

      const initial = {};
      for (const p of safe) {
        const pid = String(p?.sessionParticipantId ?? p?.id);
        const cur = p?.current || {};
        initial[pid] = {
          status: backendToUiStatus(cur?.status ?? 'PENDING'),
          note: cur?.note || '',
          selectedMemberPlanId: null,
          limitDecision: null,
          packageExceedPolicy: null,
        };
      }
      setByPerson(initial);
    };

    setLoading(true);

    const pFetch =
      typeof fetchParticipants === 'function'
        ? Promise.resolve(fetchParticipants(sessionId))
        : attendanceApi
            .getSessionParticipants(sessionId, peopleFetchLimit)
            .then((r) => r?.participants ?? []);

    pFetch
      .then((list) => {
        if (!alive) return;
        initFromList(list);
      })
      .catch((e) => {
        if (!alive) return;
        setPeople([]);
        setByPerson({});
        setSubmitError(e);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
    // ⚠️ fetchParticipants dependency'si parent'ta inline function ise her render değişir.
    // Bu yüzden open+sessionId odaklı çalışmak genelde daha stabil.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId, peopleFetchLimit, resetUi]);

  // ===== validation =====
  const invalidCount = useMemo(() => {
    return Object.values(byPerson).filter(
      (v) => requiresNote(v?.status) && !String(v?.note || '').trim()
    ).length;
  }, [byPerson]);

  // ===== payload =====
  const payload = useMemo(() => {
    if (!sessionId) return null;

    return {
      sessionId,
      items: (people || []).map((p) => {
        const pid = String(p?.sessionParticipantId ?? p?.id);
        const v = byPerson?.[pid] || { status: S.PENDING, note: '' };

        return {
          sessionParticipantId: p?.sessionParticipantId ?? p?.id,
          attendanceStatus: uiStatusToBackend(v.status),
          attendanceNote: String(v.note || '').trim() || null,
          selectedMemberPlanId: v.selectedMemberPlanId ?? null,
          limitDecision: v.limitDecision ?? null,
          packageExceedPolicy: v.packageExceedPolicy ?? null,
        };
      }),
    };
  }, [sessionId, people, byPerson]);

  // ===== commit (tek yer) =====
  const commit = useCallback(
    async ({ onSave, onClose } = {}) => {
      if (!payload) return;

      const res = await attendanceApi.commitMark(payload);
      // onSave payload forward ✅
      onSave?.(res);
      onClose?.();
      return res;
    },
    [payload]
  );

  // ===== validate + commit =====
  const validateAndMaybeCommit = useCallback(
    async ({ onSave, onClose } = {}) => {
      if (!payload) return;
      if (invalidCount > 0) return;

      resetUi();

      try {
        setSaveLoading(true);

        const dec = await attendanceApi.validateMark(payload);
        setDecision(dec);

        // allowed değil -> modal aç
        if (!dec?.allowed) {
          setDecisionModalOpen(true);
          setPendingCommit(false);
          return;
        }

        // warning/action varsa -> modal aç, commit bekle
        if (hasWarningsOrActions(dec)) {
          setDecisionModalOpen(true);
          setPendingCommit(true);
          return;
        }

        // direkt commit
        await commit({ onSave, onClose });
      } catch (e) {
        setSubmitError(e);
      } finally {
        setSaveLoading(false);
      }
    },
    [payload, invalidCount, resetUi, commit]
  );

  // ===== confirm after decision =====
  const confirmDecisionAndCommit = useCallback(
    async ({ onSave, onClose, forceCommit = false } = {}) => {
      if (!payload) return;

      try {
        setSaveLoading(true);
        setSubmitError(null);

        // forceCommit değilse ve pendingCommit true değilse,
        // yanlışlıkla commit basılmasını engelle (UX güvenliği)
        if (!forceCommit && !pendingCommit) {
          // yine de commit etmek istersen forceCommit kullan
          return;
        }

        await commit({ onSave, onClose });
      } catch (e) {
        setSubmitError(e);
      } finally {
        setSaveLoading(false);
        setPendingCommit(false);
      }
    },
    [payload, pendingCommit, commit]
  );

  return {
    loading,
    people,
    byPerson,
    setByPerson,

    invalidCount,

    saveLoading,
    decision,
    submitError,

    decisionModalOpen,
    setDecisionModalOpen,
    pendingCommit,

    validateAndMaybeCommit,
    confirmDecisionAndCommit,

    resetDecision: resetUi,
  };
}
